import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {LenderUser, RepOnlineStatus} from 'app/models/dto/lender-user.model';
import { SessionService } from 'app/modules/session/services/session.service';
import {
  GoOffline,
  GoOfflineSubscription,
  GoOnline,
  GoOnlineSubscription
} from 'app/store/lender-user/lender-user/lender-user.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { environment } from 'environments/environment';
import {Observable, Subject} from 'rxjs';
import { Cookie } from 'ng2-cookies';
import { utcToZonedTime } from 'date-fns-tz';
import {take, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent implements OnInit, OnDestroy {

  @Select(LenderUserState.online) onlineStatus$: Observable<boolean>;
  @Input() user: LenderUser;

  /** URL to the System Admin application */
  canAccessSystemAdmin: boolean;
  canManageAssignmentGroups: boolean;
  canRingDrawDeals: boolean;
  canSetUserToAvailable: boolean;
  canViewAutoSeqProto: boolean;
  canViewBusinessMap: boolean;
  destroyed$ = new Subject<boolean>();
  eveningWindowDate: Date;
  morningWindowDate: Date;
  online: boolean = false;
  onlineCheckInterval: any;
  sysAdminUrl: string;

  constructor(
    private sessionService: SessionService,
    private store: Store
  ) {
    this.sysAdminUrl = environment.environment === 'alpha'
      ? environment.API_URL + `/nova/login?access_token=${Cookie.get('auth_token')}`
      : environment.API_URL + '/nova';
  }

  ngOnInit() {
    this.store.dispatch(new GoOnlineSubscription(this.user.id) );
    this.canAccessSystemAdmin = this.user.permissions.includes('accessSystemAdmin');
    this.canManageAssignmentGroups = this.user.permissions.includes('manageAssignmentGroups');
    this.canRingDrawDeals = this.user.permissions.includes('ringDrawDeals');
    this.canViewBusinessMap = this.user.permissions.includes('canViewBusinessMap');
    this.canViewAutoSeqProto = this.user.permissions.includes('canViewAutoSeqProto');
    this.setOnlineParams();
  }

  setOnlineParams() {
    const mountainTimeDate = utcToZonedTime(new Date(), 'America/Denver'),
      year = mountainTimeDate.getFullYear(),
      month = mountainTimeDate.getMonth(),
      day = mountainTimeDate.getDate();

    this.morningWindowDate = new Date(year, month, day, 6, 45, 0);
    this.eveningWindowDate = new Date(year, month, day, 16, 45, 0);
    this.checkOnlineStatus();
    //check online status every minute to show status display based on time windows
    this.onlineCheckInterval = setInterval(() => this.checkOnlineStatus(), 60000);
    //also subscribe to changes that come from state
    this.onlineStatus$
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe(() => this.checkOnlineStatus());
  }

  checkOnlineStatus() {
    this.onlineStatus$.pipe(take(1))
      .subscribe(onlineStatus => {
        const now = utcToZonedTime(new Date(), 'America/Denver');

        this.canSetUserToAvailable = this.user.permissions.includes('canSetUserToAvailable') &&
          now > this.morningWindowDate &&
          now < this.eveningWindowDate;

        this.online = onlineStatus && now < this.eveningWindowDate;
      });
  }

  goOnline() {
    this.store.dispatch(new GoOnline(this.user.id));
  }

  goOffline() {
    this.store.dispatch(new GoOffline(this.user.id));
  }

  logout() {
    this.sessionService.logout();
  }

  ngOnDestroy() {
    clearInterval(this.onlineCheckInterval);
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(new GoOfflineSubscription(this.user.id) );
  }
}
